import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';

// Branded "choose your password" page. Two kinds of link land here:
//  ?t=<welcome token>                         Resi welcome email (24h, single use; /api/set-password trades it for a Firebase code)
//  ?mode=resetPassword&oobCode=<code>         Firebase's own email, once the console action URL points here
// After saving, the agent is signed in and goes to the dashboard (the first-sign-in tour shows there as usual).
const GOLD = '#D4AF37';
export const MIN_LEN = 8;
export const passwordProblems = (pw, again) => {
  const p = [];
  if (pw.length < MIN_LEN) p.push(`At least ${MIN_LEN} characters`);
  if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) p.push('Use letters and at least one number');
  if (again !== undefined && pw !== again) p.push('Passwords match');
  return p;
};
const api = async (body) => {
  const r = await fetch('/api/set-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  return r.json().catch(() => ({ ok: false, reason: 'error' }));
};
const wrap = { minHeight: '100vh', background: '#f5f3ee', fontFamily: 'Arial, sans-serif', color: '#111', padding: '32px 16px', boxSizing: 'border-box' };
const card = { maxWidth: 440, margin: '0 auto', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5dcc8', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' };
const input = { width: '100%', boxSizing: 'border-box', padding: '12px 64px 12px 12px', fontSize: 16, border: '1px solid #ccc', borderRadius: 8 };
const primary = { width: '100%', minHeight: 48, background: GOLD, color: '#000', border: 0, borderRadius: 8, fontWeight: 700, fontSize: 16, cursor: 'pointer' };
const showBtn = { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, color: '#8C6D1F', fontWeight: 700, cursor: 'pointer', padding: 8, fontSize: 13 };

export function SetPasswordView({ state, email, pw, again, show, busy, error, note, setPw, setAgain, setShow, onSubmit, onResend, resendEmail, setResendEmail, needsEmail }) {
  const problems = passwordProblems(pw, again);
  const rules = [`At least ${MIN_LEN} characters`, 'Use letters and at least one number', 'Passwords match'];
  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ background: '#000', padding: '20px 16px', textAlign: 'center' }}><img src="/residence-exp-logo-white.png" alt="RESIDENCE | eXp Realty" style={{ width: 240, maxWidth: '80%', height: 'auto' }} /></div>
        <div style={{ padding: 24 }}>
          {state === 'loading' && <p style={{ textAlign: 'center' }}>Checking your link…</p>}
          {state === 'form' && (
            <form onSubmit={(e) => { e.preventDefault(); if (!problems.length && !busy) onSubmit(); }} noValidate>
              <h1 style={{ fontSize: 22, margin: '0 0 6px' }}>Welcome to Resi</h1>
              <p style={{ margin: '0 0 18px', color: '#555', fontSize: 15 }}>Choose your password{email ? <> for <strong>{email}</strong></> : ''}. You'll be signed in right after.</p>
              <label htmlFor="sp-pw" style={{ fontWeight: 700, fontSize: 14 }}>New password</label>
              <div style={{ position: 'relative', margin: '6px 0 14px' }}>
                <input id="sp-pw" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} style={input} autoFocus />
                <button type="button" style={showBtn} onClick={() => setShow(!show)} aria-label={show ? 'Hide passwords' : 'Show passwords'}>{show ? 'Hide' : 'Show'}</button>
              </div>
              <label htmlFor="sp-pw2" style={{ fontWeight: 700, fontSize: 14 }}>Type it again</label>
              <div style={{ position: 'relative', margin: '6px 0 12px' }}>
                <input id="sp-pw2" type={show ? 'text' : 'password'} autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} style={input} />
              </div>
              <ul aria-label="Password rules" style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', fontSize: 14 }}>
                {rules.map((r) => { const ok = !problems.includes(r) && (r !== 'Passwords match' || again.length > 0); return <li key={r} style={{ color: ok ? '#1F6B3F' : '#666', padding: '2px 0' }}>{ok ? '✓' : '○'} {r}</li>; })}
              </ul>
              {error && <p role="alert" style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}
              <button type="submit" style={{ ...primary, opacity: problems.length || busy ? 0.55 : 1 }} disabled={!!problems.length || busy}>{busy ? 'Saving…' : 'Save password and sign in'}</button>
            </form>
          )}
          {state === 'expired' && (
            <div style={{ textAlign: 'center' }}>
              <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>This link has expired</h1>
              <p style={{ color: '#555', lineHeight: 1.5 }}>Set-password links work once and only for a limited time. No problem, we'll send you a fresh one.</p>
              {needsEmail && <input type="email" placeholder="Your email" aria-label="Your email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} style={{ ...input, padding: 12, margin: '8px 0 12px' }} />}
              {note ? <p role="status" style={{ color: '#1F6B3F' }}>{note}</p> : <button style={primary} disabled={busy} onClick={onResend}>{busy ? 'Sending…' : 'Send me a new link'}</button>}
              {error && <p role="alert" style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}
              <p style={{ fontSize: 14, marginTop: 18 }}>Already set it? <a href="/">Sign in</a></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const t = params.get('t'); const oob = params.get('oobCode');
  const [state, setState] = useState('loading');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState(''); const [again, setAgain] = useState(''); const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [note, setNote] = useState('');
  const [resendEmail, setResendEmail] = useState('');

  useEffect(() => {
    (async () => {
      if (oob) { try { setEmail(await verifyPasswordResetCode(auth, oob)); setState('form'); } catch (e) { setState('expired'); } return; }
      if (t) { const r = await api({ t, action: 'peek' }); setState(r.ok ? 'form' : 'expired'); return; }
      setState('expired');
    })();
  }, [t, oob]);

  const onSubmit = async () => {
    setBusy(true); setError('');
    try {
      let code = oob; let who = email;
      if (!code) {
        const r = await api({ t, action: 'start' });
        if (!r.ok) { setState('expired'); setBusy(false); return; }
        code = r.oobCode; who = r.email; setEmail(r.email);
      }
      await confirmPasswordReset(auth, code, pw);
      await signInWithEmailAndPassword(auth, who, pw);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      const c = e && e.code;
      if (c === 'auth/expired-action-code' || c === 'auth/invalid-action-code') setState('expired');
      else if (c === 'auth/weak-password') setError('Please choose a stronger password.');
      else setError('Something went wrong saving your password. Please try again.');
      setBusy(false);
    }
  };
  const onResend = async () => {
    setBusy(true); setError('');
    try {
      if (t) { const r = await api({ t, action: 'resend' }); if (r.ok) setNote('Done. Check your email for sign-in instructions.'); else setError(r.reason === 'already_signed_in' ? 'Your account is already set up. Sign in at resisystem.com/login, or use Forgot password.' : r.reason === 'already_resent' ? 'A new link was already sent for this one. Check your inbox, or ask your Resi admin.' : 'We could not send a new link. Please ask your Resi admin to resend your welcome email.'); }
      else { if (!/\S+@\S+\.\S+/.test(resendEmail)) { setError('Please enter your email.'); setBusy(false); return; } await sendPasswordResetEmail(auth, resendEmail.trim(), { url: 'https://resisystem.com/dashboard' }).catch(() => {}); setNote('If that email has a Resi account, a new link is on its way.'); }
    } catch (e) { setError('We could not send a new link. Please try again.'); }
    setBusy(false);
  };
  return <SetPasswordView {...{ state, email, pw, again, show, busy, error, note, setPw, setAgain, setShow, onSubmit, onResend, resendEmail, setResendEmail }} needsEmail={!t} />;
}
